import {DOCUMENT} from '@angular/common';
import {Component, Inject, OnInit} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {HeaderIdentifier} from '@application/headers/header-identifier.enum';
import {ErrorHandlers} from '@application/helper/error-handlers';
import {LastModifiedCardUtils} from '@application/helper/last-modified-card-utils';
import {NavigationHelperService} from '@application/helper/navigation-helper/navigation-helper.service';
import {RouteUtils} from '@application/helper/routing/route-utils';
import {UnsavedChangesNotifierService} from '@application/helper/unsaved-changes-notifier/unsaved-changes-notifier.service';
import {AsyncUniqueValidator} from '@application/validators/async-unique-validator';
import {WeavingMachine} from '@domain/machine/weaving-machine';
import {Permission} from '@domain/profile/permission.enum';
import {Subscription as ProfileSubscription} from '@domain/profile/subscription';
import {WeaveProduct} from '@domain/textile-data/weave-product/weave-product';
import {AUTHENTICATION, Authentication} from '@infrastructure/http/authentication/authentication';
import {MACHINES, Machines} from '@infrastructure/http/machine/machines.interface';
import {WEAVE_PRODUCT, WeaveProducts} from '@infrastructure/http/weave-product/weave-products.interface';
import {NavigationMachineOverviewData} from '@presentation/pages/machine-overview/navigation-machine-overview-data.interface';
import {
  AssertionUtils,
  BackendError,
  BaseComponent,
  DialogBuilderFactoryService,
  DialogType,
  FormValidationHelper,
  ObjectUtils,
  SaveType,
  skeletonViewAnimation,
  TranslateService
} from '@vdw/angular-component-library';
import {isNil, isUndefined} from 'lodash-es';
import {Observable} from 'rxjs';
import {finalize, takeUntil} from 'rxjs/operators';
import {OverviewListCreel} from '../../creel/overview/overview-list-creel';
import {TextileService} from '../../textile-data-overview/textile.service';
import {TextileDataType} from '../../textile-data-type.enum';

@Component({
  templateUrl: './add-weave-product-page.component.html',
  styleUrls: ['./add-weave-product-page.component.scss'],
  animations: [skeletonViewAnimation('.form-field-skeleton-wrapper, .mat-card-header, .button-skeleton-wrapper,  .text-skeleton-wrapper, .colored-yarn-set-item header, .ag-root')]
})
export class AddWeaveProductPageComponent extends BaseComponent implements OnInit {
  public readonly translationKeyForWeaveProducts = 'TEXTILE_DATA.WEAVE_PRODUCT.WEAVE_PRODUCT';
  public readonly SAVE_TYPE = SaveType;
  public readonly HEADER_IDENTIFIER = HeaderIdentifier.ADD_WEAVE_PRODUCT;
  public texstyleEditPermission: Permission = LastModifiedCardUtils.getPermissionToModifyItems('weaveProduct');

  public addWeaveProductForm: FormGroup<{
    name: FormControl<string>;
    version: FormControl<number>;
    widthInCm: FormControl<number>;
    pickDensityInPicksPerCm: FormControl<number>;
    standardSpeedInRotationsPerMinute: FormControl<number>;
    standardEfficiencyInPercentage: FormControl<number>;
    numberOfFeeders: FormControl<number>;
    numberOfShafts: FormControl<number>;
    description: FormControl<string>;
  }>;

  public weaveProductToSave: WeaveProduct;
  public isLoadingWeaveProduct = true;
  public weaveProductIsLocked = false;
  public maxWidthInCm: number;
  public maxNumberOfFeeders: number;
  public maxNumberOfShafts: number;

  private readonly WEAVE_PRODUCT_OVERVIEW_PAGE_URL = RouteUtils.paths.texStyle.weaveProduct.absolutePath;
  private readonly EDIT_WEAVE_PRODUCT_PAGE_URL = RouteUtils.paths.texStyle.weaveProduct.editWeaveProduct.path;
  private readonly DUPLICATE_WEAVE_PRODUCT_PAGE_URL = RouteUtils.paths.texStyle.weaveProduct.duplicateWeaveProduct.path;
  private urlParams: Params;

  private currentSubscription: ProfileSubscription;

  private saveButtonTouched = false;

  public constructor(
    @Inject(MACHINES) private readonly machines: Machines,
    @Inject(WEAVE_PRODUCT) public weaveProducts: WeaveProducts,
    @Inject(AUTHENTICATION) private authentication: Authentication,
    @Inject(DOCUMENT) private document: Document,
    private readonly translate: TranslateService,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly formBuilder: FormBuilder,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly navigationHelperService: NavigationHelperService<OverviewListCreel>,
    private readonly textileService: TextileService,
    private readonly unsavedChangesNotifierService: UnsavedChangesNotifierService
  ) {
    super();
  }

  public get action(): string {
    return this.isEditingWeaveProduct() ? 'GENERAL.ACTIONS.EDIT_OBJECT' : 'GENERAL.ACTIONS.CREATE_OBJECT';
  }

  public ngOnInit(): void {
    this.currentSubscription = this.authentication.getCurrentSubscription();
    this.urlParams = this.activatedRoute.snapshot.params;
    this.weaveProductToSave = WeaveProduct.createEmptyWeaveProduct();
    this.setWeaveProductForm();
    this.getEmitValue();

    if (!isUndefined(this.urlParams.id)) {
      this.weaveProducts
        .getById(parseInt(this.urlParams.id, 10))
        .pipe(takeUntil(this.unSubscribeOnViewDestroy))
        .subscribe({
          next: (weaveProduct: WeaveProduct) => {
            this.weaveProductToSave = weaveProduct;
            this.setValues(this.isDuplicatingWeaveProduct() ? null : this.weaveProductToSave.name);
            this.isLoadingWeaveProduct = false;
            this.weaveProductIsLocked = this.isEditingWeaveProduct() && this.weaveProductToSave.used;
            this.disableForm();
          },
          error: ErrorHandlers.navigateToOverviewAndThrowError(this.router, this.WEAVE_PRODUCT_OVERVIEW_PAGE_URL, this.navigationHelperService)
        });
    } else {
      this.setValues(this.weaveProductToSave.name);
      this.setFormFieldsForMachine();
      this.isLoadingWeaveProduct = false;
    }
  }

  public onNavigationHelperDestroy(): void {
    this.navigationHelperService.onDestroy();
  }

  public canShowAddWeaveProductForm(): boolean {
    return !isNil(this.addWeaveProductForm);
  }

  public hasEditPermission(): boolean {
    return this.currentSubscription?.hasPermission(this.texstyleEditPermission);
  }

  public navigateBack(): void {
    this.navigationHelperService.navigateToPreviousRoute(this.WEAVE_PRODUCT_OVERVIEW_PAGE_URL);
  }

  public saveWeaveProduct(saveType: SaveType, navigateToSelectedRoute?: string): void {
    this.saveButtonTouched = true;
    const isValid = new FormValidationHelper().checkForms([this.addWeaveProductForm], this.document);
    if (isValid) {
      const weaveProductToSave: WeaveProduct = this.getCurrentWeaveProduct();
      this.saving = true;
      if (this.isEditingWeaveProduct()) {
        weaveProductToSave.id = this.weaveProductToSave.id;
      }

      const request: Observable<void | number> = this.isEditingWeaveProduct() ? this.weaveProducts.update(weaveProductToSave) : this.weaveProducts.save(weaveProductToSave);
      request.pipe(takeUntil(this.unSubscribeOnViewDestroy), finalize(this.finalizeSaving())).subscribe({
        next: (id: number) => {
          this.unsavedChangesNotifierService.notifyChangesToForm(false);
          this.textileService.navigateAndShowToast(
            saveType,
            TextileDataType.WEAVE_PRODUCT,
            this.translationKeyForWeaveProducts,
            this.isEditingWeaveProduct(),
            weaveProductToSave.name,
            id,
            navigateToSelectedRoute
          );
        },
        error: (errorMessage: BackendError) => this.showErrorDialogForBackendError(this.isEditingWeaveProduct() ? 'GENERAL.ACTIONS.EDIT_OBJECT' : 'GENERAL.ACTIONS.CREATE_OBJECT', errorMessage.message)
      });
    }
  }

  public deleteWeaveProduct(): void {
    this.textileService.removeConfirmation(this.weaveProductToSave, TextileDataType.WEAVE_PRODUCT, false, null, this.weaveProducts);
  }

  public duplicateWeaveProduct(): void {
    this.router.navigateByUrl(RouteUtils.paths.texStyle.weaveProduct.duplicateWeaveProduct.absolutePath.replace(':id', this.weaveProductToSave.id.toString()));
  }

  public openConflictsDialog(): void {
    this.textileService.getConflicts(this.weaveProductToSave.id, this.weaveProductToSave.name, this.weaveProducts, LastModifiedCardUtils.getTranslations(TextileDataType.WEAVE_PRODUCT));
  }

  public getVersionValue(): number {
    return !this.isEditingWeaveProduct() ? 1 : this.weaveProductToSave.version;
  }

  public canShowInvalidFormMessageError(): boolean {
    const isFormInvalid = Object.values(this.addWeaveProductForm.controls).some((control: FormControl) => control.invalid && control.touched);
    if (!isFormInvalid) {
      this.saveButtonTouched = false;
    }
    return isFormInvalid && this.saveButtonTouched;
  }

  public isEditingWeaveProduct(): boolean {
    return this.activatedRoute.snapshot.routeConfig.path === this.EDIT_WEAVE_PRODUCT_PAGE_URL;
  }

  public isWeaveProductBeingUsed(): boolean {
    return !isUndefined(this.weaveProductToSave) && this.weaveProductToSave.used && !this.isDuplicatingWeaveProduct();
  }

  public unlockWeaveProduct(): void {
    this.weaveProductIsLocked = false;
    this.addWeaveProductForm.enable();
    this.addWeaveProductForm.controls.name.disable();
    this.addWeaveProductForm.controls.version.patchValue(this.weaveProductToSave.version);
  }

  public getLoadLink(): string {
    return ErrorHandlers.getLoadLink(RouteUtils.paths.texStyle.weaveProduct.editWeaveProduct.absolutePath, this.addWeaveProductForm.controls['name']);
  }

  private isDuplicatingWeaveProduct(): boolean {
    return this.activatedRoute.snapshot.routeConfig.path === this.DUPLICATE_WEAVE_PRODUCT_PAGE_URL;
  }

  private getCurrentWeaveProduct(): WeaveProduct {
    const name: string = this.addWeaveProductForm.getRawValue().name;
    const version: number = this.isEditingWeaveProduct() ? this.addWeaveProductForm.value.version : 1;
    const widthInCm: number = this.addWeaveProductForm.value.widthInCm;
    const pickDensityInPicksPerCm: number = this.addWeaveProductForm.value.pickDensityInPicksPerCm;
    const standardSpeedInRotationsPerMinute: number = this.addWeaveProductForm.value.standardSpeedInRotationsPerMinute;
    const standardEfficiencyInPercentage: number = this.addWeaveProductForm.value.standardEfficiencyInPercentage;
    const numberOfFeeders: number = this.addWeaveProductForm.value.numberOfFeeders;
    const numberOfShafts: number = this.addWeaveProductForm.value.numberOfShafts;
    const description: string = this.addWeaveProductForm.value.description;

    const used = this.isEditingWeaveProduct() && this.weaveProductToSave.used;

    return new WeaveProduct(
      null,
      name,
      version,
      widthInCm,
      pickDensityInPicksPerCm,
      standardSpeedInRotationsPerMinute,
      standardEfficiencyInPercentage,
      numberOfFeeders,
      numberOfShafts,
      description,
      used
    );
  }

  private showErrorDialogForBackendError(translationKey: string, message: string): void {
    this.dialogBuilderFactoryService.getBuilder().openAlertDialog({
      titleText: this.translate.instant(translationKey, {
        object: this.translate.instant(this.translationKeyForWeaveProducts, {
          count: 1
        })
      }),
      messageText: message,
      type: DialogType.INFORMATION
    });
  }

  private setWeaveProductForm(): void {
    this.addWeaveProductForm = this.formBuilder.group({
      name: this.formBuilder.control(null, {updateOn: 'change', validators: [Validators.required]}),
      version: this.formBuilder.control(1),
      widthInCm: this.formBuilder.control(null, {updateOn: 'change', validators: [Validators.required, Validators.min(1)]}),
      pickDensityInPicksPerCm: this.formBuilder.control(null, {updateOn: 'change', validators: [Validators.required, Validators.min(1)]}),
      standardSpeedInRotationsPerMinute: this.formBuilder.control(null, {updateOn: 'change', validators: [Validators.min(1)]}),
      standardEfficiencyInPercentage: this.formBuilder.control(null, {updateOn: 'change', validators: [Validators.min(0), Validators.max(100)]}),
      numberOfFeeders: this.formBuilder.control(null, {updateOn: 'change', validators: [Validators.required, Validators.min(0), Validators.max(32)]}),
      numberOfShafts: this.formBuilder.control(null, {updateOn: 'change', validators: [Validators.required, Validators.min(0), Validators.max(32)]}),
      description: this.formBuilder.control(null)
    });
    this.unsavedChangesNotifierService.notifyChanges(this.addWeaveProductForm, this.unSubscribeOnViewDestroy);
  }

  private setValues(weaveProductName: string): void {
    this.addWeaveProductForm.reset({
      name: weaveProductName,
      version: this.weaveProductToSave.version,
      widthInCm: this.weaveProductToSave.widthInCm,
      pickDensityInPicksPerCm: this.weaveProductToSave.pickDensityInPicksPerCm,
      standardSpeedInRotationsPerMinute: this.weaveProductToSave.standardSpeedInRotationsPerMinute,
      standardEfficiencyInPercentage: this.weaveProductToSave.standardEfficiencyInPercentage,
      numberOfFeeders: this.weaveProductToSave.numberOfFeeders,
      numberOfShafts: this.weaveProductToSave.numberOfShafts,
      description: this.weaveProductToSave.description
    });

    this.setWeaveProductNameAsyncValidator(weaveProductName);
  }

  private setWeaveProductNameAsyncValidator(name: string = null): void {
    this.addWeaveProductForm.controls.name.setAsyncValidators([AsyncUniqueValidator.createValidator(this.weaveProducts, name)]);
  }

  private disableForm(): void {
    if (!this.hasEditPermission() || this.weaveProductIsLocked) {
      this.addWeaveProductForm.disable();
    }

    if (this.isEditingWeaveProduct()) {
      this.addWeaveProductForm.controls.name.disable();
    }
  }

  private setFormFieldsForMachine(): void {
    const emptyMachineOverviewState = {equipmentId: null} as NavigationMachineOverviewData;
    const machineOverviewState = this.navigationHelperService.getPartialState<NavigationMachineOverviewData>(Object.keys(emptyMachineOverviewState), true);

    if (!AssertionUtils.isNullOrUndefined(machineOverviewState) && !ObjectUtils.isDeepEqual(machineOverviewState, emptyMachineOverviewState)) {
      this.machines
        .getById(machineOverviewState.equipmentId)
        .pipe(takeUntil(this.unSubscribeOnViewDestroy))
        .subscribe((weavingMachine: WeavingMachine) => {
          this.maxWidthInCm = weavingMachine.weavingMachineType.width;
          this.addWeaveProductForm.controls.widthInCm.addValidators(Validators.max(this.maxWidthInCm));
          this.addWeaveProductForm.controls.widthInCm.updateValueAndValidity();
          this.maxNumberOfFeeders = weavingMachine.numberOfFeeders;
          this.addWeaveProductForm.controls.numberOfFeeders.addValidators(Validators.max(this.maxNumberOfFeeders));
          this.addWeaveProductForm.controls.numberOfFeeders.updateValueAndValidity();
          this.maxNumberOfShafts = weavingMachine.numberOfShafts;
          this.addWeaveProductForm.controls.numberOfShafts.addValidators(Validators.max(this.maxNumberOfShafts));
          this.addWeaveProductForm.controls.numberOfShafts.updateValueAndValidity();
        });
    }
  }

  private getEmitValue(): void {
    this.unsavedChangesNotifierService
      .getSaveEventEmitter()
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((res: {action: boolean; navigateToSelectedRoute: string}) => {
        if (res) {
          this.saveWeaveProduct(SaveType.SAVE_AND_CLOSE, res.navigateToSelectedRoute);
        }
      });
  }
}
